/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { IAudioSurah } from '@/infrastructure/interfaces/i-audio-surah';
import { useRouter } from 'next/router';
import { AudioSurah } from '@/infrastructure/class/audio-surah';
import AudioSurahsListItem from './item/index';
import { sortArrayByFieldName } from '@/utils/helpers/sort-array-by-field-name';
import AppTitle from '@/components/common/title';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import Link from 'next/link';
import { Button } from '@mui/material';
import { ExpandMoreRounded } from '@mui/icons-material';
import { paginate } from '@/utils/helpers/paginate';
import { readSurahPaginationLimit } from '@/infrastructure/const/pagination';
import s from './styles.module.scss';
import { Playlist } from '@/infrastructure/class/playlist';
import { usePlayer } from '@/infrastructure/context/player-context';

interface IAudioSurahsListProps {
  items: IAudioSurah[];
  href: string;
  partial: boolean;
  playlist?: Playlist;
  reciterSlug?: string;
  serverPage?: number;
  className?: string;
}

const AudioSurahsList: React.FC<IAudioSurahsListProps> = (props) => {
  const { items, href, partial, playlist, reciterSlug, className = '' } = props;
  const { setTracks } = usePlayer();
  const router = useRouter();
  const { locale, query } = router;
  const { page = 1 } = query;

  const [localPage, setLocalPage] = useState<number>(Number(page));
  const [localItems, setLocalItems] = useState<IAudioSurah[]>(
    paginate(items, readSurahPaginationLimit, localPage)
  );
  const pageCount = Math.ceil(items.length / readSurahPaginationLimit);
  const hasItems =
    localItems.length !== items.length && Number(page) < pageCount;

  const audioSurahs = useMemo<AudioSurah[]>(() => {
    return sortArrayByFieldName(
      localItems.map((audioSurah) => new AudioSurah(audioSurah, locale)),
      'number'
    );
  }, [localItems, locale]);

  const { t: tCommon } = useTranslation('common');
  const { t: tButtons } = useTranslation('buttons');

  const title = `${tCommon('tracks')}`;

  const wrapperClassNames = classNames(s['wrapper'], className);

  const onItemClick = (track: AudioSurah) => {
    setTracks(audioSurahs, track);
  };

  const onShowMore = async () => {
    setLocalPage(localPage + 1);
    const newItems = paginate(items, readSurahPaginationLimit, localPage + 1);
    setLocalItems([...localItems, ...newItems]);
  };

  const onSeeAll = async () => {
    setLocalItems(items);
  };

  useEffect(() => {
    setLocalItems(paginate(items, readSurahPaginationLimit, localPage));
  }, [items]);

  return (
    <div className={wrapperClassNames}>
      <div className={s['title']}>
        <AppTitle title={title} />
        {hasItems && (
          <span onClick={onSeeAll} className={s['link']}>
            {tButtons('seeAll')}
          </span>
        )}
      </div>
      <table className={s['table']}>
        <tbody className={s['table-body']}>
          {audioSurahs.map((audioSurah, index) => (
            <AudioSurahsListItem
              key={audioSurah.slug}
              data={audioSurah}
              needTopBorder={index < 2}
              playlist={playlist}
              reciterSlug={reciterSlug}
              partial={partial}
              onClick={() => onItemClick(audioSurah)}
            />
          ))}
        </tbody>
      </table>
      {hasItems ? (
        <div className={s['bottom']}>
          <Link
            href={{
              pathname: href,
              query: {
                page: localPage + 1,
              },
            }}
            shallow={true}
            scroll={false}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <Button
              id="pagination-button"
              aria-controls="pagination-button"
              color="error"
              onClick={onShowMore}
              size="medium"
              classes={{
                root: s['pagination-btn'],
              }}
              endIcon={<ExpandMoreRounded />}
            >
              {tButtons('showMore')}
            </Button>
          </Link>
        </div>
      ) : (
        <div className={s['bottom']} />
      )}
    </div>
  );
};

export default AudioSurahsList;
